import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Save, Trash2, Power, PowerOff, Loader2, Trophy, Sparkles, Crown, Mail, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { endpoints } from "@/api";
import { useI18n } from "@/i18n";
import ImageUploader from "@/components/ImageUploader";
import { toast } from "sonner";

const METHODS = ["v60", "chemex", "espresso", "aeropress", "frenchpress", "moka"];
const todayPlus = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};
const blank = () => ({
  title_ar: "تحدّي V60 الأسبوعي",
  title_en: "Weekly V60 Challenge",
  description_ar: "حقق نسبة استخلاص بين 19-21% على V60.",
  description_en: "Hit an extraction yield between 19-21% on V60.",
  method: "v60",
  ey_min: 19,
  ey_max: 21,
  start_at: todayPlus(0),
  end_at: todayPlus(7),
  image_url: "",
  is_active: true,
});

export default function AdminChallengeTab() {
  const { isRTL } = useI18n();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin-challenges"],
    queryFn: endpoints.adminListChallenges,
  });
  const challenges = data?.items || [];
  const [draft, setDraft] = useState(null);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);

  const startCreate = () => {
    setDraft(blank());
    setEditId(null);
  };
  const startEdit = (c) => {
    setDraft({
      ...c,
      start_at: (c.start_at || "").slice(0, 10),
      end_at: (c.end_at || "").slice(0, 10),
    });
    setEditId(c.id);
  };
  const cancel = () => {
    setDraft(null);
    setEditId(null);
  };

  const save = async () => {
    if (!draft) return;
    if (Number(draft.ey_min) >= Number(draft.ey_max)) {
      toast.error(isRTL ? "EY الأدنى يجب أن يكون أقل من EY الأعلى" : "ey_min must be < ey_max");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...draft,
        ey_min: Number(draft.ey_min),
        ey_max: Number(draft.ey_max),
        start_at: new Date(draft.start_at).toISOString(),
        end_at: new Date(draft.end_at + "T23:59:59").toISOString(),
        is_active: !!draft.is_active,
      };
      if (editId) {
        await endpoints.adminUpdateChallenge(editId, payload);
        toast.success(isRTL ? "تم التحديث" : "Challenge updated");
      } else {
        await endpoints.adminCreateChallenge(payload);
        toast.success(isRTL ? "تم الإنشاء" : "Challenge created");
      }
      qc.invalidateQueries({ queryKey: ["admin-challenges"] });
      qc.invalidateQueries({ queryKey: ["active-challenge"] });
      cancel();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isRTL ? "تعذّر الحفظ" : "Save failed"));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (c) => {
    try {
      await endpoints.adminUpdateChallenge(c.id, {
        ...c,
        is_active: !c.is_active,
      });
      qc.invalidateQueries({ queryKey: ["admin-challenges"] });
      qc.invalidateQueries({ queryKey: ["active-challenge"] });
      toast.success(isRTL ? "تم التحديث" : "Updated");
    } catch (e) {
      toast.error(isRTL ? "تعذّر التحديث" : "Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm(isRTL ? "هل أنت متأكد من الحذف؟ سيتم حذف كل المشاركات أيضاً." : "Delete this challenge and all submissions?")) return;
    try {
      await endpoints.adminDeleteChallenge(id);
      qc.invalidateQueries({ queryKey: ["admin-challenges"] });
      qc.invalidateQueries({ queryKey: ["active-challenge"] });
      toast.success(isRTL ? "تم الحذف" : "Deleted");
    } catch {
      toast.error(isRTL ? "تعذّر الحذف" : "Delete failed");
    }
  };

  const [applyingPreset, setApplyingPreset] = useState(false);
  const applyV60HotPreset = async () => {
    if (!window.confirm(isRTL
      ? "سيتم إيقاف أي تحدّي نشط حالياً وتطبيق تحدّي V60 الساخن بكامل تفاصيله (الجائزة، التحكيم اليدوي، البث على Instagram). هل تريد المتابعة؟"
      : "This will deactivate any currently active challenge and apply the V60 Hot preset (prize, manual judging, Instagram live). Continue?")) return;
    setApplyingPreset(true);
    try {
      const res = await endpoints.adminApplyV60HotPreset();
      qc.invalidateQueries({ queryKey: ["admin-challenges"] });
      qc.invalidateQueries({ queryKey: ["active-challenge"] });
      toast.success(isRTL
        ? (res.refreshed ? "تم تحديث تحدّي V60 الساخن" : "تم تطبيق تحدّي V60 الساخن")
        : (res.refreshed ? "V60 Hot challenge refreshed" : "V60 Hot challenge applied"));
    } catch (e) {
      toast.error(isRTL ? "تعذّر تطبيق التحدّي" : "Failed to apply preset");
    } finally {
      setApplyingPreset(false);
    }
  };

  const [applyingIcePreset, setApplyingIcePreset] = useState(false);
  const applyV60IcePreset = async () => {
    if (!window.confirm(isRTL
      ? "سيتم إيقاف أي تحدّي نشط حالياً وتطبيق تحدّي V60 المثلّج بكامل تفاصيله (وصفة كاملة، جائزة، تحكيم يدوي، بث على Instagram). هل تريد المتابعة؟"
      : "This will deactivate any currently active challenge and apply the V60 Iced preset (full recipe submission, prize, manual judging, Instagram live). Continue?")) return;
    setApplyingIcePreset(true);
    try {
      const res = await endpoints.adminApplyV60IcePreset();
      qc.invalidateQueries({ queryKey: ["admin-challenges"] });
      qc.invalidateQueries({ queryKey: ["active-challenge"] });
      toast.success(isRTL
        ? (res.refreshed ? "تم تحديث تحدّي V60 المثلّج" : "تم تطبيق تحدّي V60 المثلّج")
        : (res.refreshed ? "V60 Iced challenge refreshed" : "V60 Iced challenge applied"));
    } catch (e) {
      toast.error(isRTL ? "تعذّر تطبيق التحدّي" : "Failed to apply preset");
    } finally {
      setApplyingIcePreset(false);
    }
  };

  const [winnerForChallenge, setWinnerForChallenge] = useState(null);
  const [broadcastingId, setBroadcastingId] = useState(null);

  const broadcastEmail = async (c) => {
    const ok = window.confirm(
      isRTL
        ? `إرسال إيميل للجميع عن «${c.title_ar}»؟ راح يوصل لكل المستخدمين المسجّلين.`
        : `Email every user about "${c.title_en}"? This will hit all registered users.`
    );
    if (!ok) return;
    setBroadcastingId(c.id);
    try {
      const res = await endpoints.adminBroadcastChallengeEmail(c.id);
      toast.success(
        isRTL
          ? `أُرسل لـ ${res.sent} مستخدم${res.failed ? ` · فشل ${res.failed}` : ""} (من أصل ${res.total})`
          : `Sent to ${res.sent} users${res.failed ? ` · ${res.failed} failed` : ""} (of ${res.total})`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isRTL ? "تعذّر الإرسال" : "Broadcast failed"));
    } finally {
      setBroadcastingId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-challenges-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl text-[#2A2421] font-semibold flex items-center gap-2">
            <Trophy className="w-5 h-5 text-[#C86240]" /> {isRTL ? "تحدّيات الأسبوع" : "Weekly Challenges"}
          </h2>
          <p className="text-xs text-[#5C544F] mt-1">
            {isRTL ? "تحدّي واحد فعّال في كل فترة. المشاركون يكسبون نقاطاً عند مطابقة EY." : "One active challenge at a time. Users earn points by matching the EY target."}
          </p>
        </div>
        {!draft && (
          <div className="flex gap-2 flex-wrap">
            <Button
              onClick={applyV60HotPreset}
              disabled={applyingPreset}
              variant="outline"
              className="rounded-full border-[#C86240] text-[#C86240] hover:bg-[#C86240]/10"
              data-testid="apply-v60-hot-preset-btn"
              title={isRTL ? "تطبيق تحدّي V60 الساخن جاهز" : "Apply V60 Hot preset"}
            >
              {applyingPreset
                ? <Loader2 className="w-4 h-4 me-2 animate-spin" />
                : <Sparkles className="w-4 h-4 me-2" />}
              {isRTL ? "تطبيق تحدّي V60 الساخن" : "Apply V60 Hot preset"}
            </Button>
            <Button
              onClick={applyV60IcePreset}
              disabled={applyingIcePreset}
              variant="outline"
              className="rounded-full border-sky-500 text-sky-600 hover:bg-sky-50"
              data-testid="apply-v60-ice-preset-btn"
              title={isRTL ? "تطبيق تحدّي V60 المثلّج جاهز" : "Apply V60 Iced preset"}
            >
              {applyingIcePreset
                ? <Loader2 className="w-4 h-4 me-2 animate-spin" />
                : <Sparkles className="w-4 h-4 me-2" />}
              {isRTL ? "تطبيق تحدّي V60 المثلّج" : "Apply V60 Iced preset"}
            </Button>
            <Button onClick={startCreate} className="rounded-full bg-[#C86240] hover:bg-[#C86240]/90 text-white" data-testid="admin-challenge-new-btn">
              <Plus className="w-4 h-4 me-2" /> {isRTL ? "تحدي جديد" : "New challenge"}
            </Button>
          </div>
        )}
      </div>

      {draft && (
        <div className="soft-card rounded-2xl p-6 space-y-4" data-testid="admin-challenge-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <FormField label={isRTL ? "العنوان (عربي)" : "Title (Arabic)"}>
              <Input value={draft.title_ar} onChange={(e) => setDraft({ ...draft, title_ar: e.target.value })} data-testid="ch-title-ar" />
            </FormField>
            <FormField label={isRTL ? "العنوان (إنجليزي)" : "Title (English)"}>
              <Input value={draft.title_en} onChange={(e) => setDraft({ ...draft, title_en: e.target.value })} data-testid="ch-title-en" />
            </FormField>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <FormField label={isRTL ? "الوصف (عربي)" : "Description (Arabic)"}>
              <Textarea rows={3} value={draft.description_ar} onChange={(e) => setDraft({ ...draft, description_ar: e.target.value })} data-testid="ch-desc-ar" />
            </FormField>
            <FormField label={isRTL ? "الوصف (إنجليزي)" : "Description (English)"}>
              <Textarea rows={3} value={draft.description_en} onChange={(e) => setDraft({ ...draft, description_en: e.target.value })} data-testid="ch-desc-en" />
            </FormField>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <FormField label={isRTL ? "الطريقة" : "Method"}>
              <Select value={draft.method} onValueChange={(v) => setDraft({ ...draft, method: v })}>
                <SelectTrigger data-testid="ch-method"><SelectValue /></SelectTrigger>
                <SelectContent>{METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </FormField>
            <FormField label={`EY ${isRTL ? "الأدنى" : "min"} %`}>
              <Input type="number" step="0.1" value={draft.ey_min} onChange={(e) => setDraft({ ...draft, ey_min: e.target.value })} data-testid="ch-ey-min" />
            </FormField>
            <FormField label={`EY ${isRTL ? "الأعلى" : "max"} %`}>
              <Input type="number" step="0.1" value={draft.ey_max} onChange={(e) => setDraft({ ...draft, ey_max: e.target.value })} data-testid="ch-ey-max" />
            </FormField>
            <FormField label={isRTL ? "نشط" : "Active"}>
              <Select value={draft.is_active ? "1" : "0"} onValueChange={(v) => setDraft({ ...draft, is_active: v === "1" })}>
                <SelectTrigger data-testid="ch-active"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">{isRTL ? "نعم" : "Active"}</SelectItem>
                  <SelectItem value="0">{isRTL ? "لا" : "Draft"}</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FormField label={isRTL ? "تاريخ البداية" : "Start date"}>
              <Input type="date" value={draft.start_at} onChange={(e) => setDraft({ ...draft, start_at: e.target.value })} data-testid="ch-start" />
            </FormField>
            <FormField label={isRTL ? "تاريخ النهاية" : "End date"}>
              <Input type="date" value={draft.end_at} onChange={(e) => setDraft({ ...draft, end_at: e.target.value })} data-testid="ch-end" />
            </FormField>
          </div>
          <FormField label={isRTL ? "صورة التحدي (اختياري)" : "Cover image (optional)"}>
            <ImageUploader value={draft.image_url} onChange={(v) => setDraft({ ...draft, image_url: v })} testid="ch-image" />
          </FormField>
          <div className="flex gap-3 pt-2">
            <Button onClick={save} disabled={busy} className="bg-[#2A2421] hover:bg-[#2A2421]/90 text-[#F9F6F0] rounded-full" data-testid="ch-save-btn">
              {busy ? <Loader2 className="w-4 h-4 me-2 animate-spin" /> : <Save className="w-4 h-4 me-2" />}
              {editId ? (isRTL ? "تحديث" : "Update") : (isRTL ? "إنشاء" : "Create")}
            </Button>
            <Button onClick={cancel} variant="outline" className="rounded-full" data-testid="ch-cancel-btn">
              {isRTL ? "إلغاء" : "Cancel"}
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {isLoading && <div className="text-sm text-[#5C544F]"><Loader2 className="w-4 h-4 inline animate-spin me-2" />{isRTL ? "جاري التحميل..." : "Loading..."}</div>}
        {!isLoading && challenges.length === 0 && !draft && (
          <div className="text-center py-12 text-sm text-[#5C544F] border border-dashed border-[#2A2421]/15 rounded-2xl">
            {isRTL ? "لا توجد تحدّيات بعد. أنشئ التحدي الأول." : "No challenges yet. Create your first one."}
          </div>
        )}
        {challenges.map((c) => (
          <div key={c.id} className="soft-card rounded-2xl p-5 flex flex-wrap items-center gap-3" data-testid={`admin-challenge-${c.id}`}>
            <div className="flex-1 min-w-[260px]">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs uppercase tracking-widest text-[#4A5D4E]">{c.method}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${c.is_active ? "bg-emerald-100 text-emerald-800" : "bg-stone-100 text-stone-600"}`}>
                  {c.is_active ? (isRTL ? "نشط" : "Active") : (isRTL ? "مسوّدة" : "Draft")}
                </span>
                <span className="text-[10px] text-[#C86240] font-semibold">EY {c.ey_min}–{c.ey_max}%</span>
              </div>
              <div className="text-[#2A2421] font-semibold mt-1">{isRTL ? c.title_ar : c.title_en}</div>
              <div className="text-xs text-[#5C544F] mt-0.5">
                {new Date(c.start_at).toLocaleDateString()} → {new Date(c.end_at).toLocaleDateString()}
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button onClick={() => setWinnerForChallenge(c)} variant="outline" size="sm" className="rounded-full border-amber-400 text-amber-700 hover:bg-amber-50" data-testid={`ch-winner-${c.id}`}>
                <Crown className="w-3 h-3 me-1" />
                {c.winner_user_id
                  ? (isRTL ? "تغيير الفائز" : "Change winner")
                  : (isRTL ? "إعلان الفائز" : "Announce winner")}
              </Button>
              <Button
                onClick={() => broadcastEmail(c)}
                disabled={broadcastingId === c.id}
                variant="outline"
                size="sm"
                className="rounded-full border-sky-400 text-sky-700 hover:bg-sky-50"
                data-testid={`ch-broadcast-${c.id}`}
                title={isRTL ? "أرسل إيميل عن هذا التحدّي لجميع المستخدمين" : "Email every user about this challenge"}
              >
                {broadcastingId === c.id ? <Loader2 className="w-3 h-3 me-1 animate-spin" /> : <Mail className="w-3 h-3 me-1" />}
                {isRTL ? "إيميل للجميع" : "Email all"}
              </Button>
              <Button onClick={() => toggleActive(c)} variant="outline" size="sm" className="rounded-full" data-testid={`ch-toggle-${c.id}`}>
                {c.is_active ? <PowerOff className="w-3 h-3 me-1" /> : <Power className="w-3 h-3 me-1" />}
                {c.is_active ? (isRTL ? "إيقاف" : "Disable") : (isRTL ? "تشغيل" : "Enable")}
              </Button>
              <Button onClick={() => startEdit(c)} variant="outline" size="sm" className="rounded-full" data-testid={`ch-edit-${c.id}`}>
                {isRTL ? "تعديل" : "Edit"}
              </Button>
              <Button onClick={() => remove(c.id)} variant="outline" size="sm" className="rounded-full text-rose-600 hover:bg-rose-50 border-rose-200" data-testid={`ch-delete-${c.id}`}>
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {winnerForChallenge && (
        <AnnounceWinnerDialog
          challenge={winnerForChallenge}
          onClose={() => setWinnerForChallenge(null)}
          onSuccess={() => {
            qc.invalidateQueries({ queryKey: ["admin-challenges"] });
            qc.invalidateQueries({ queryKey: ["active-challenge"] });
          }}
          isRTL={isRTL}
        />
      )}
    </div>
  );
}

function AnnounceWinnerDialog({ challenge, onClose, onSuccess, isRTL }) {
  const [selected, setSelected] = useState(challenge.winner_user_id || null);
  const [prizeText, setPrizeText] = useState(
    challenge.winner_prize || (isRTL ? challenge.prize_ar : challenge.prize_en) || ""
  );
  const [customMessage, setCustomMessage] = useState(challenge.winner_message || "");
  const [sending, setSending] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-challenge-submissions", challenge.id],
    queryFn: () => endpoints.adminListChallengeSubmissions(challenge.id),
  });
  const submissions = data?.submissions || [];

  // Aggregate per-user best score (in case of multiple submissions per user)
  const participants = React.useMemo(() => {
    const map = new Map();
    for (const s of submissions) {
      const uid = s.user_id;
      const prev = map.get(uid);
      if (!prev || (s.score || 0) > (prev.score || 0)) {
        map.set(uid, {
          user_id: uid,
          user_name: s.user_name,
          user_email: s.user_email,
          user_picture: s.user_picture,
          score: s.score || 0,
          ey_pct: s.ey_pct,
          tds_pct: s.tds_pct,
          recipe_id: s.recipe_id,
          recipe_title_ar: s.recipe_title_ar,
          recipe_title_en: s.recipe_title_en,
          attempts: 1,
        });
      } else {
        prev.attempts = (prev.attempts || 1) + 1;
      }
    }
    return Array.from(map.values()).sort((a, b) => (b.score || 0) - (a.score || 0));
  }, [submissions]);
  const isRecipeMode = challenge.submission_mode === "recipe";

  const confirm = async () => {
    if (!selected) {
      toast.error(isRTL ? "اختر فائزاً أولاً" : "Pick a winner first");
      return;
    }
    const winnerObj = participants.find((p) => p.user_id === selected);
    const name = winnerObj?.user_name || winnerObj?.user_email || "";
    const ok = window.confirm(
      isRTL
        ? `هل تريد إعلان ${name} فائزاً؟ سيُمنح شارة "بطل V60 الساخن" ويُرسَل له إيميل تهنئة فوراً.`
        : `Announce ${name} as the winner? They'll get the "V60 Hot Champion" badge and a congratulations email.`
    );
    if (!ok) return;
    setSending(true);
    try {
      const res = await endpoints.adminAnnounceWinner(challenge.id, {
        winner_user_id: selected,
        prize_text: prizeText || null,
        custom_message: customMessage || null,
      });
      toast.success(
        isRTL
          ? `تم إعلان الفائز${res?.email_dispatched ? " · أُرسل الإيميل" : ""}${res?.badge_awarded ? " · مُنحت الشارة" : ""}`
          : `Winner announced${res?.email_dispatched ? " · email sent" : ""}${res?.badge_awarded ? " · badge granted" : ""}`
      );
      onSuccess?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isRTL ? "تعذّر الإعلان" : "Announce failed"));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="announce-winner-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#2A2421]">
            <Crown className="w-5 h-5 text-amber-500" />
            {isRTL ? "إعلان فائز التحدّي" : "Announce Challenge Winner"}
          </DialogTitle>
          <p className="text-xs text-[#5C544F] mt-1">
            {isRTL ? challenge?.title_ar : challenge?.title_en}
          </p>
        </DialogHeader>

        <div className="space-y-4">
          {challenge?.winner_user_id && (
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 flex items-center gap-2" data-testid="winner-already-announced">
              <Crown className="w-4 h-4 text-amber-500" />
              {isRTL ? `الفائز الحالي: ${challenge.winner_user_name || challenge.winner_user_id}` : `Current winner: ${challenge.winner_user_name || challenge.winner_user_id}`}
            </div>
          )}

          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-[#5C544F]">
              {isRTL ? "المشاركون" : "Participants"} {participants.length > 0 && `(${participants.length})`}
            </Label>
            <div className="mt-2 max-h-[280px] overflow-y-auto border border-[#2A2421]/10 rounded-xl divide-y divide-[#2A2421]/5" data-testid="winner-participants-list">
              {isLoading && (
                <div className="p-4 text-sm text-[#5C544F] text-center">
                  <Loader2 className="w-4 h-4 inline animate-spin me-2" />{isRTL ? "جاري التحميل..." : "Loading..."}
                </div>
              )}
              {!isLoading && participants.length === 0 && (
                <div className="p-6 text-sm text-[#5C544F] text-center" data-testid="no-participants">
                  {isRTL ? "لا توجد مشاركات بعد." : "No submissions yet."}
                </div>
              )}
              {!isLoading && participants.map((p, idx) => (
                <label
                  key={p.user_id}
                  className={`flex items-center gap-3 p-3 cursor-pointer transition-colors ${selected === p.user_id ? "bg-amber-50" : "hover:bg-stone-50"}`}
                  data-testid={`winner-option-${p.user_id}`}
                >
                  <input
                    type="radio"
                    name="winner"
                    value={p.user_id}
                    checked={selected === p.user_id}
                    onChange={() => setSelected(p.user_id)}
                    className="accent-amber-500"
                    data-testid={`winner-radio-${p.user_id}`}
                  />
                  {idx < 3 && (
                    <span className="text-lg w-6 text-center">{idx === 0 ? "🥇" : idx === 1 ? "🥈" : "🥉"}</span>
                  )}
                  {p.user_picture ? (
                    <img src={p.user_picture} alt="" className="w-9 h-9 rounded-full object-cover border border-[#2A2421]/10" />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-[#C86240]/15 flex items-center justify-center text-[#C86240] text-sm font-semibold">
                      {(p.user_name || "?").slice(0, 1).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[#2A2421] truncate">{p.user_name || (isRTL ? "بدون اسم" : "Unnamed")}</div>
                    <div className="text-[11px] text-[#5C544F] truncate">{p.user_email || "—"}</div>
                    {p.recipe_id && (
                      <a
                        href={`/recipes?recipe=${encodeURIComponent(p.recipe_id)}`}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1 text-[10px] text-[#C86240] hover:underline mt-0.5 font-medium"
                        data-testid={`view-recipe-${p.user_id}`}
                      >
                        <Crown className="w-2.5 h-2.5" />
                        {isRTL ? "افتح وصفته" : "View recipe"} · {isRTL ? (p.recipe_title_ar || "—") : (p.recipe_title_en || p.recipe_title_ar || "—")}
                      </a>
                    )}
                  </div>
                  <div className="text-right">
                    {isRecipeMode ? (
                      <div className="text-[10px] text-[#5C544F]">{isRTL ? "وصفة" : "Recipe"}</div>
                    ) : (
                      <>
                        <div className="text-sm font-bold text-[#C86240]">{Math.round(p.score)} pts</div>
                        <div className="text-[10px] text-[#5C544F]">EY {Number(p.ey_pct || 0).toFixed(1)}% · {p.attempts} {isRTL ? "محاولة" : "tries"}</div>
                      </>
                    )}
                  </div>
                </label>
              ))}
            </div>
          </div>

          <FormField label={isRTL ? "نص الجائزة (يظهر في الإيميل)" : "Prize text (shown in email)"}>
            <Input
              value={prizeText}
              onChange={(e) => setPrizeText(e.target.value)}
              placeholder={isRTL ? "محصولان (٢٥٠ جرام × ٢) — نصف كيلو" : "Two 250g bags — 500g specialty coffee"}
              data-testid="winner-prize-input"
            />
          </FormField>

          <FormField label={isRTL ? "رسالة شخصية (اختياري)" : "Personal message (optional)"}>
            <Textarea
              rows={3}
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              placeholder={isRTL ? "رسالة قصيرة من عبدالله للفائز..." : "A short note from Abdullah to the winner..."}
              data-testid="winner-message-input"
            />
          </FormField>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button onClick={onClose} variant="outline" className="rounded-full" data-testid="winner-cancel-btn">
            <X className="w-4 h-4 me-1" /> {isRTL ? "إلغاء" : "Cancel"}
          </Button>
          <Button
            onClick={confirm}
            disabled={sending || !selected}
            className="rounded-full bg-amber-500 hover:bg-amber-600 text-white"
            data-testid="winner-announce-btn"
          >
            {sending ? <Loader2 className="w-4 h-4 me-2 animate-spin" /> : <Mail className="w-4 h-4 me-2" />}
            {isRTL ? "إعلان وإرسال الإيميل" : "Announce & send email"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const FormField = ({ label, children }) => (
  <div>
    <Label className="text-xs uppercase tracking-[0.2em] text-[#5C544F]">{label}</Label>
    <div className="mt-2">{children}</div>
  </div>
);
